/**
 * Maps low-level Web Serial / esptool failures to actionable user-facing
 * messages. Pure function so the whole matrix is unit-testable.
 */

export interface MappedError {
  title: string;
  detail: string;
}

export function describeSerialError(err: unknown): MappedError {
  const name = err instanceof Error ? err.name : String((err as { name?: string })?.name ?? '');
  const message = err instanceof Error ? err.message : String(err ?? '');
  const haystack = `${name} ${message}`.toLowerCase();

  if (name === 'NotFoundError' || haystack.includes('no port selected')) {
    return {
      title: 'No board selected',
      detail: 'The port picker was closed without choosing a board. Plug the board in and try again.',
    };
  }
  if (name === 'InvalidStateError') {
    return {
      title: 'Port is busy',
      detail: 'Another tab or program (e.g. a serial monitor) is using this port. Close it and retry.',
    };
  }
  if (name === 'SecurityError') {
    return {
      title: 'Serial access blocked',
      detail: 'Permission to use serial ports was denied. Re-grant access in the browser site settings.',
    };
  }
  if (name === 'NetworkError' || haystack.includes('device disconnected') || haystack.includes('port is not open')) {
    return {
      title: 'Board disconnected',
      detail: 'The board dropped off the USB bus during the operation. Reconnect it and retry; the bootloader is not damaged.',
    };
  }
  if (haystack.includes('abort')) {
    return { title: 'Cancelled', detail: 'The operation was cancelled.' };
  }
  if (
    haystack.includes('failed to connect') ||
    haystack.includes('timed out') ||
    haystack.includes('failed to sync') ||
    haystack.includes('sync')
  ) {
    return {
      title: 'Could not enter the bootloader',
      detail: 'Hold the BOOT button, tap EN/RST once, release BOOT, then retry. Also try a lower Flash Baud (460800) and a data-capable USB cable.',
    };
  }
  if (haystack.includes('packet content mismatch') || haystack.includes('checksum')) {
    return {
      title: 'Flash write failed',
      detail: 'Data did not verify during flashing. Lower the Flash Baud to 460800, use a shorter/high-quality cable, and retry.',
    };
  }
  if (haystack.includes('no serial ports') || haystack.includes('navigator.serial')) {
    return {
      title: 'Web Serial unavailable',
      detail: 'This browser does not support the Web Serial API. Use Chrome or Edge (version 90 or newer).',
    };
  }
  return {
    title: 'Serial operation failed',
    detail: message || 'An unexpected serial error occurred. Reconnect the board and try again.',
  };
}

/** True when the current browser exposes the Web Serial API. */
export function isWebSerialSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}
